import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Inbox } from "lucide-react";
import { capabilities } from "../../lib/access";
import { api, type DashboardWithMeta } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/primitives";
import { useShareResponse } from "./shareResponse";

/**
 * Dashboards shared with you that aren't in your sidebar: new ones waiting for an answer, and ones
 * you set aside or removed. Accepting one adds it to the sidebar.
 */
export function SharedWithMePage() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const dashboards = useQuery({ queryKey: ["dashboards"], queryFn: api.dashboards });
  const can = capabilities(me.data);
  const pending = dashboards.data?.filter((d) => d.shareStatus === "pending") ?? [];
  const dismissed = dashboards.data?.filter((d) => d.shareStatus === "dismissed") ?? [];

  return (
    <div className="mx-auto max-w-[900px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 flex flex-wrap items-center gap-3 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><Inbox className="size-4 text-subtle" /> Shared with {can.viewAs ? "them" : "me"}</h1>
        <span className="text-sm text-muted">Shared dashboards appear in the sidebar once they're added.</span>
      </header>
      {dashboards.isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : dashboards.error ? (
        <p className="text-sm text-bad">{dashboards.error.message}</p>
      ) : !pending.length && !dismissed.length ? (
        <p className="mt-16 text-center text-sm text-muted">Nothing waiting. Every dashboard shared with {can.viewAs ? "them" : "you"} is in the sidebar.</p>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && <Section title="New" rows={pending} canRespond={can.write} />}
          {dismissed.length > 0 && <Section title="Not in the sidebar" rows={dismissed} canRespond={can.write} />}
        </div>
      )}
    </div>
  );
}

function Section({ title, rows, canRespond }: { title: string; rows: DashboardWithMeta[]; canRespond: boolean }) {
  const respond = useShareResponse();
  const busy = (id: string) => respond.isPending && respond.variables?.id === id;
  return (
    <section>
      <h2 className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">{title}</h2>
      <ul className="divide-y divide-border overflow-hidden rounded-[14px] border border-border bg-surface">
        {rows.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-[1_1_12rem]">
              <Link to="/d/$id" params={{ id: d.id }} className="block truncate text-sm font-medium hover:underline">{d.title}</Link>
              <span className="text-xs text-muted">Shared by {d.sharedBy}</span>
            </div>
            {canRespond && (
              <div className="flex items-center gap-2">
                {d.shareStatus === "pending" && (
                  <Button size="sm" variant="ghost" disabled={busy(d.id)} onClick={() => respond.mutate({ id: d.id, status: "dismissed" })}>
                    Not now
                  </Button>
                )}
                <Button size="sm" disabled={busy(d.id)} onClick={() => respond.mutate({ id: d.id, status: "accepted" })}>
                  <Check className="size-3.5" /> Add to sidebar
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {respond.error && <p className="mt-2 text-xs text-bad">{respond.error.message}</p>}
    </section>
  );
}
